import type { Mix } from '@/types'

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Archive, Share2, User } from 'lucide-react'
import { Button, Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui'

export interface MixCardProps {
  mix: Mix
  onShare?: () => void
  onArchive?: () => void
}

export default function MixCard({ mix, onShare, onArchive }: MixCardProps) {
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  // Extract up to 4 images from recipes that have images (newest first)
  const images = useMemo(() => {
    return [...mix.recipes]
      .sort((a, b) => {
        if (a.updatedAt < b.updatedAt) return 1
        if (a.updatedAt > b.updatedAt) return -1
        return 0
      })
      .filter((r) => !!r.image)
      .map((r) => r.image!)
      .slice(0, 4)
  }, [mix.recipes])

  const imageCount = images.length
  const totalRecipeImages = mix.recipes.filter((r) => !!r.image).length
  const hasMoreImages = totalRecipeImages > 4

  const relativeTime = useMemo(() => {
    if (!mix.updatedAt) return ''
    try {
      return formatDistanceToNow(new Date(mix.updatedAt), { addSuffix: false })
    } catch {
      return ''
    }
  }, [mix.updatedAt])

  return (
    <article
      className="relative group rounded-xl border border-border bg-card overflow-hidden transition-all duration-200 hover-supported:hover:shadow-md hover-supported:hover:scale-[1.02] hover-supported:hover:border-primary/50"
      aria-label={`Mix: ${mix.name}`}
    >
      {/* Header: avatar + name + time + recipe count */}
      <div className="px-3 py-3 flex items-center gap-2">
        {mix.ownerImage ? (
          <img
            src={mix.ownerImage}
            alt={mix.ownerName}
            className="h-8 w-8 rounded-full object-cover bg-muted flex-shrink-0"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-semibold truncate text-foreground">{mix.ownerName}</span>
          {relativeTime && (
            <span className="text-xs text-muted-foreground">last updated {relativeTime} ago</span>
          )}
        </div>
        <span className="inline-flex items-center bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0">
          {mix.recipes.length} recipe{mix.recipes.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Image collage */}
      <Link to={`/mixes/${mix.id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          {imageCount === 0 && (
            <div className="h-full w-full flex items-center justify-center">
              <span className="text-muted-foreground text-sm">🍳 {mix.recipes.length} recipe{mix.recipes.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          {imageCount === 1 && (
            <img
              src={images[0]}
              alt={`Recipe from ${mix.name}`}
              className="h-full w-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          )}

          {imageCount === 2 && (
            <div className="grid grid-cols-2 gap-0.5 h-full">
              {images.map((src, i) => (
                <img key={i} src={src} alt="" className="h-full w-full object-cover" />
              ))}
            </div>
          )}

          {imageCount === 3 && (
            <div className="grid grid-cols-2 grid-rows-2 gap-0.5 h-full">
              <img
                src={images[0]}
                alt=""
                className="h-full w-full object-cover row-span-2 col-span-1"
              />
              <img src={images[1]} alt="" className="h-full w-full object-cover" />
              <img src={images[2]} alt="" className="h-full w-full object-cover" />
            </div>
          )}

          {imageCount >= 4 && (
            <div className="grid grid-cols-2 grid-rows-2 gap-0.5 h-full">
              {[0, 1, 2].map((i) => (
                <img
                  key={i}
                  src={images[i]}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              ))}
              <div className="relative h-full w-full">
                <img
                  src={images[3]}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
                {hasMoreImages && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white text-lg font-semibold">+{totalRecipeImages - 3}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Link>

      {/* Actions bar */}
      <div className="px-3 pt-4 pb-2 flex items-center gap-4">
        {onShare && (
          <button
            type="button"
            aria-label="Share mix"
            onClick={(e) => { e.stopPropagation(); onShare() }}
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            <Share2 className="h-5 w-5" />
          </button>
        )}

        {onArchive && (
          <button
            type="button"
            aria-label="Archive mix"
            onClick={(e) => { e.stopPropagation(); setShowArchiveConfirm(true) }}
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer ml-auto"
          >
            <Archive className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Footer: mix name */}
      <div className="px-3 pb-3">
        <Link
          to={`/mixes/${mix.id}`}
          className="text-sm font-medium truncate text-foreground hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {mix.name}
        </Link>
      </div>

      {/* Archive confirmation dialog */}
      {showArchiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowArchiveConfirm(false)}>
          <div className="mx-4 w-full max-w-sm">
            <Card onClick={(e) => e.stopPropagation()}>
              <CardHeader className="text-center">
                <CardTitle>Archive Mix</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground text-center">
                  Are you sure you want to archive <span className="font-medium text-foreground">{mix.name}</span>?
                </p>
              </CardContent>
              <CardFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowArchiveConfirm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={() => { onArchive?.(); setShowArchiveConfirm(false) }}
                  className="flex-1"
                >
                  Archive
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      )}
    </article>
  )
}
